<template>
  <Dialog
    :visible="show"
    modal
    :closable="false"
    :close-on-escape="false"
    :dismissable-mask="false"
    :draggable="false"
    :style="{ width: '32rem', maxWidth: '95vw' }"
    :header="`Rolling in Mardi Gras ${paradeYear}?`"
  >
    <div class="kb-form">
      <p class="kb-dialog-lead">
        Hi {{ member?.name || 'red shirt' }}, we don't know yet whether you're
        rolling with us for <strong>Mardi Gras {{ paradeYear }}</strong>.
      </p>
      <p class="kb-field-hint">
        Tell us so the roster and parade-day messages are right. You can change
        your answer any time in Settings.
      </p>
    </div>

    <template #footer>
      <div class="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 w-full">
        <Button
          label="No, not this year"
          severity="secondary"
          outlined
          size="small"
          :disabled="submitting"
          :loading="submittingValue === 'No'"
          @click="respond('No')"
        />
        <Button
          label="Yes, I'm rolling"
          size="small"
          :disabled="submitting"
          :loading="submittingValue === 'Yes'"
          @click="respond('Yes')"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import type { Member } from '~/shared/types/member'
import { isRedshirt } from '~/shared/types/memberType.js'
import {
  getMemberRollingStatusRaw,
  mergeMemberRollingForYear,
  type MemberRollingByYear,
  type MemberRollingStatus,
} from '~/shared/utils/memberRolling.js'

interface Props {
  member: Member | null
}

const props = defineProps<Props>()

const membersStore = useMembersStore()
const toast = useToast()
const { paradeYear } = useParadeYear()

const submitting = ref(false)
const submittingValue = ref<MemberRollingStatus | null>(null)

const show = computed(() => {
  const m = props.member
  if (!m || !isRedshirt(m)) return false
  return getMemberRollingStatusRaw(
    m as { rolling_by_year?: MemberRollingByYear },
    paradeYear.value,
  ) === undefined
})

const respond = async (value: MemberRollingStatus) => {
  const m = props.member
  if (!m || submitting.value) return
  submitting.value = true
  submittingValue.value = value
  try {
    const merged = mergeMemberRollingForYear(
      m as { rolling_by_year?: MemberRollingByYear },
      paradeYear.value,
      value,
    )
    const success = await membersStore.updateMember(m, { rolling_by_year: merged })
    if (!success) {
      toast.add({
        severity: 'error',
        summary: 'Update failed',
        detail: 'Could not save your answer. Please try again.',
        life: 4000,
      })
    }
  }
  catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: err instanceof Error ? err.message : String(err),
      life: 4000,
    })
  }
  finally {
    submitting.value = false
    submittingValue.value = null
  }
}
</script>
